import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ROUTES, buildProviderPatientPath } from "@/routes";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { HeartHandshake, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";

// Risk answer mappings — answers that indicate a critical social need
const CRITICAL_RISKS: Record<string, { answers: string[]; label: string }> = {
  es_food_insecurity: { answers: ["Sometimes true", "Often true"], label: "Food insecurity" },
  es_utility_difficulty: { answers: ["Yes", "Already shut off"], label: "Utility hardship" },
  nbe_housing: { answers: ["I have a place to live, but I am worried about losing it", "I do not have a steady place to live"], label: "Housing instability" },
  nbe_safety: { answers: ["A little unsafe", "Very unsafe"], label: "Unsafe neighborhood" },
  hcaq_transportation: { answers: ["Yes"], label: "Transportation barriers" },
  scc_safety_threat: { answers: ["Sometimes", "Often"], label: "Safety concern" },
  scc_social_support: { answers: ["Rarely", "Never"], label: "Social isolation" },
};

function parseScreenings(screenings: string[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const entry of screenings) {
    const idx = entry.indexOf("::");
    if (idx > -1) {
      result[entry.substring(0, idx)] = entry.substring(idx + 2);
    }
  }
  return result;
}

function getFlaggedRisks(screenings: string[]): string[] {
  const parsed = parseScreenings(screenings);
  const flags: string[] = [];
  for (const [qId, config] of Object.entries(CRITICAL_RISKS)) {
    const answer = parsed[qId];
    if (!answer) continue;
    const answers = answer.split("||");
    if (answers.some((a) => config.answers.includes(a))) {
      flags.push(config.label);
    }
  }
  return flags;
}

interface PatientWithRisks {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  risks: string[];
}

export function SdohDashboardWidget() {
  const navigate = useNavigate();

  const { data: patientsWithRisks, isLoading } = useQuery({
    queryKey: ["sdoh-dashboard-risks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patients")
        .select("id, user_id, first_name, last_name, sdoh_screenings")
        .not("sdoh_screenings", "eq", "{}");

      if (error) throw error;

      const results: PatientWithRisks[] = [];
      for (const row of data || []) {
        const screenings = (row.sdoh_screenings as string[]) || [];
        if (screenings.length === 0) continue;
        const risks = getFlaggedRisks(screenings);
        if (risks.length > 0) {
          results.push({
            id: row.id,
            user_id: row.user_id,
            first_name: row.first_name,
            last_name: row.last_name,
            risks,
          });
        }
      }
      // Sort by most risks first
      results.sort((a, b) => b.risks.length - a.risks.length);
      return results;
    },
  });

  const totalPatients = patientsWithRisks?.length || 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <HeartHandshake className="h-5 w-5 text-primary" />
            SDOH Needs
          </CardTitle>
          {totalPatients > 0 && (
            <Badge variant="destructive" className="text-xs">
              {totalPatients} Patient{totalPatients !== 1 ? "s" : ""}
            </Badge>
          )}
        </div>
        <CardDescription>Patients with critical social needs</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : totalPatients === 0 ? (
          <div className="text-center py-6 text-sm text-muted-foreground">
            No patients with flagged social needs
          </div>
        ) : (
          <div className="space-y-3">
            {patientsWithRisks!.slice(0, 5).map((p) => {
              const initials = `${p.first_name?.[0] || ""}${p.last_name?.[0] || ""}`.toUpperCase();
              return (
                <div
                  key={p.id}
                  className="flex items-start gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate(buildProviderPatientPath(p.user_id))}
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {p.first_name} {p.last_name}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {p.risks.slice(0, 3).map((risk) => (
                        <Badge
                          key={risk}
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 border-orange-300 bg-orange-50 text-orange-800 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-700"
                        >
                          <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                          {risk}
                        </Badge>
                      ))}
                      {p.risks.length > 3 && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          +{p.risks.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {totalPatients > 5 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                onClick={() => navigate(ROUTES.PROVIDER_PATIENTS)}
              >
                View all {totalPatients} patients
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
